import { useCallback, useEffect } from "react";
import { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useShallow } from "zustand/shallow";

import { SubscriptionRow } from "@/services/api/fetchers/subscription";
import { useBoundStore } from "@/app/store";
import { supabase } from "@/services/supabase/client";

type RealtimeTables = "subscriptions";

export function useRealtime() {
  const { setUser } = useBoundStore(useShallow((state) => ({ setUser: state.user.set })));
  const isActive = useBoundStore(useShallow((state) => state.device.isActive));

  const handleSubscriptionsPayload = useCallback(
    (payload: RealtimePostgresChangesPayload<SubscriptionRow>) => {
      switch (payload.eventType) {
        case "UPDATE":
          setUser({ subscription: payload.new });
          break;
        default:
          break;
      }
    },
    [setUser]
  );

  useEffect(() => {
    let subscription: RealtimeChannel;

    const setupRealtimeSubscription = async () => {
      await unsubscribeRealtimeConnection();
      subscription = supabase
        .channel("schema-db-changes")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
          },
          (payload) => {
            switch (payload.table as RealtimeTables) {
              case "subscriptions":
                handleSubscriptionsPayload(payload as RealtimePostgresChangesPayload<SubscriptionRow>);
                break;
              default:
                break;
            }
          }
        )
        .subscribe();
    };

    const unsubscribeRealtimeConnection = async () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };

    const handleActiveStateChange = async () => {
      if (isActive) {
        if (subscription?.state === "closed") {
          // Token refresh is important to prevent reconnection failure
          const { data } = await supabase.auth.getSession();
          if (data.session) {
            supabase.realtime.setAuth(data.session?.access_token);
            setupRealtimeSubscription();
          }
        }
      }
    };

    // Set up initial subscription
    setupRealtimeSubscription();

    // Handle active state changes
    handleActiveStateChange();

    // Cleanup
    return () => {
      unsubscribeRealtimeConnection();
    };
  }, [handleSubscriptionsPayload, isActive]);

  return null;
}
